import React from "react";
import { useNavigate } from "react-router-dom";
import { TextSearch } from "lucide-react";
import { useSearch, toUrlParams } from "../../contexts/SearchContext";
import { useOrg } from "../../contexts/OrganizationContext";
import { apiService } from "../../services/index";
import { taxonomyService } from "../../services/taxonomyService";
import { geolocate, reverseGeocode, getLocationFromResults, getServiceArea } from "../../utils/geo";
import { useAutocompleteKeyboard } from "../../hooks/useAutocompleteKeyboard";
import LocationConfirmation from "./LocationConfirmation";
import PlaceAutocomplete from "./PlaceAutocomplete";
import SearchInput from "./SearchInput";

function TransparentSearch() {
  const { params, setParams, search } = useSearch();
  const { organization } = useOrg();
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = React.useState("");
  const [locationInput, setLocationInput] = React.useState(params.location || "");
  const [suggestions, setSuggestions] = React.useState([]);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = React.useState(null);
  const [geolocating, setGeolocating] = React.useState(false);
  const [showLocationConfirm, setShowLocationConfirm] = React.useState(false);

  const suggestionsListRef = React.useRef(null);
  const locationCoordsRef = React.useRef(null);

  const { hoveredIndex, setHoveredIndex, handleKeyDown: handleSearchKeyDown } = useAutocompleteKeyboard({
    suggestions,
    showSuggestions,
    setShowSuggestions,
    onSelect: handleSuggestionSelect,
    listRef: suggestionsListRef,
  });

  const searchInputRef = React.useRef(null);
  const suggestionsRef = React.useRef(null);
  const formWrapRef = React.useRef(null);
  const geolocateButtonRef = React.useRef(null);
  const fetchIdRef = React.useRef(0);
  const debounceTimerRef = React.useRef(null);
  const preserveSearchFocusRef = React.useRef(false);

  const stateName = organization.serviceLocale?.stateName || "";

  const handleGeolocate = React.useCallback(async () => {
    setGeolocating(true);
    try {
      const coords = await geolocate();
      if (coords) {
        setParams({ coords });
        const results = await reverseGeocode(coords);
        if (results && results.length > 0) {
          const location = getLocationFromResults(results, organization);
          const serviceArea = getServiceArea(results);
          setLocationInput(location);
          setParams({ location, service_area: serviceArea.toLowerCase(), coords });
        }
      }
    } catch (err) {
      // Geolocation failed; user will need to enter location manually
    }
    setGeolocating(false);
  }, [organization, setParams, setLocationInput]);

  // Keep a stable ref so the auto-geolocate effect doesn't re-fire when
  // handleGeolocate gets a new reference (e.g. when organization re-renders).
  const handleGeolocateRef = React.useRef(handleGeolocate);
  React.useEffect(() => { handleGeolocateRef.current = handleGeolocate; }, [handleGeolocate]);

  // Auto-geolocate on mount if no coords or location
  React.useEffect(() => {
    if (!params.coords && !params.location) {
      handleGeolocateRef.current();
    }
  }, [params.coords, params.location]);

  // Attach native event listener to geolocate button (React events don't work after DOM manipulation)
  React.useEffect(() => {
    const button = geolocateButtonRef.current;
    if (!button) return;

    button.addEventListener('click', handleGeolocate);
    return () => button.removeEventListener('click', handleGeolocate);
  }, [handleGeolocate]);

  // Close suggestions on outside click
  React.useEffect(() => {
    function handleClickOutside(e) {
      if (formWrapRef.current && !formWrapRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    }

    function handleMouseUp() {
      if (!preserveSearchFocusRef.current) return;

      preserveSearchFocusRef.current = false;
      searchInputRef.current?.focus();
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const [problemStatements, setProblemStatements] = React.useState([]);

  React.useEffect(() => {
    apiService
      .get("/problem_statements")
      .then((data) => setProblemStatements(data || []))
      .catch(() => {});
  }, []);

  function filterProblemStatements(term) {
    const mapped = (items) =>
      items.map((ps) => ({
        label: ps.statement,
        taxonomy_ids: ps.taxonomy_ids,
        combined_taxonomy_ids: ps.combined_taxonomy_ids,
        type: "suggested",
      }));
    if (!term) return mapped(problemStatements);
    const lower = term.toLowerCase();
    return mapped(problemStatements.filter((ps) => ps.statement.toLowerCase().includes(lower)));
  }

  async function fetchSuggestions(term) {
    const fetchId = ++fetchIdRef.current;
    const suggested = filterProblemStatements(term);
    if (term.length < 3) {
      setSuggestions(suggested);
      if (suggested.length > 0) setShowSuggestions(true);
      return;
    }
    try {
      const taxonomies = await taxonomyService.search(term);
      if (fetchId !== fetchIdRef.current) return;
      const formatted = (taxonomies || []).map((t) => ({
        label: t.label || t.name,
        code: t.id,
        category: t.category,
        type: "taxonomy",
      }));
      setSuggestions([...suggested, ...formatted]);
      setShowSuggestions(true);
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return;
      // Taxonomy fetch failed; fall back to problem statement suggestions only
      setSuggestions(suggested);
      if (suggested.length > 0) setShowSuggestions(true);
    }
  }

  function debouncedFetchSuggestions(val) {
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => fetchSuggestions(val), 400);
  }

  // Watch for input changes via native events (Selenium/Capybara compatibility)
  React.useEffect(() => {
    const el = searchInputRef.current;
    if (!el) return;
    function onNativeInput() {
      const val = el.value;
      setSearchInput(val);
      setSelectedSuggestion(null);
      debouncedFetchSuggestions(val);
    }
    el.addEventListener("input", onNativeInput);
    return () => el.removeEventListener("input", onNativeInput);
  }, [problemStatements]);

  function handleSearchInputChange(e) {
    const val = e.target.value;
    setSearchInput(val);
    setSelectedSuggestion(null);
    debouncedFetchSuggestions(val);
  }

  function handleSearchInputFocus() {
    if (searchInput === "") {
      fetchSuggestions("");
    } else if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  }

  function closeSuggestions() {
    clearTimeout(debounceTimerRef.current);
    fetchIdRef.current += 1;
    setShowSuggestions(false);
    setHoveredIndex(null);
  }

  function handleSearchInputBlur(e) {
    if (suggestionsListRef.current?.contains(e.relatedTarget)) return;
    if (preserveSearchFocusRef.current) return;

    closeSuggestions();
  }

  function handleSuggestionsBlur(e) {
    const nextFocus = e.relatedTarget;
    if (searchInputRef.current === nextFocus || e.currentTarget.contains(nextFocus)) return;

    closeSuggestions();
  }

  function handleSuggestionsMouseDown(e) {
    if (e.target === e.currentTarget) {
      preserveSearchFocusRef.current = true;
    }
  }

  function handleSuggestionSelect(suggestion) {
    setSelectedSuggestion(suggestion);
    setSearchInput(suggestion.label);
    setShowSuggestions(false);
  }

  function handleLocationInputChange(value) {
    setLocationInput(value);
    locationCoordsRef.current = null;
  }

  function handleLocationPlaceSelect(location, coords) {
    setLocationInput(location);
    locationCoordsRef.current = coords;
    setParams({ location, coords, service_area: null });
  }

  function highlightTerm(label, term) {
    if (!term || term.length < 2) return label;
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return label.replace(new RegExp(escaped, "ig"), (match) => `<strong>${match}</strong>`);
  }

  function clearSelectedSuggestion() {
    setSelectedSuggestion(null);
    setSearchInput("");
    if (searchInputRef.current) searchInputRef.current.focus();
  }

  function handleSubmit(e) {
    e.preventDefault();

    const location = locationInput || stateName;
    // Any autocomplete selection searches by taxonomy_code only — the label is displayed
    // via categorySelections from the API response, not as a quoted text term.
    const terms = selectedSuggestion ? [] : (searchInput ? [searchInput] : []);

    // Extract city name from location for service_area (strip state abbreviation/name)
    const cityFromLocation = (location || stateName).split(",")[0].trim();
    const searchParams = {
      terms,
      location,
      coords: locationCoordsRef.current,
      service_area: cityFromLocation.toLowerCase(),
      page: 1,
    };

    if (selectedSuggestion) {
      if (selectedSuggestion.taxonomy_ids) {
        searchParams.taxonomy_code = selectedSuggestion.taxonomy_ids;
      } else if (selectedSuggestion.code) {
        searchParams.taxonomy_code = [selectedSuggestion.code];
      }
    }

    // Check if location confirmation is needed
    if (
      organization.flags?.enableLocationConfirmation &&
      location.trim().toLowerCase() === stateName.toLowerCase() &&
      !window.location.pathname.match(/search/)
    ) {
      setShowLocationConfirm(true);
      return;
    }

    doSearch(searchParams);
  }

  function doSearch(searchParams) {
    setShowLocationConfirm(false);
    // Just navigate — SearchPage will trigger the search from URL params
    navigate(toUrlParams({ ...params, ...searchParams }));
  }

  function handleLocationConfirm() {
    const terms = selectedSuggestion ? [] : (searchInput ? [searchInput] : []);
    const location = locationInput || stateName;
    const cityFromLocation = location.split(",")[0].trim();
    const searchParams = { terms, location, coords: locationCoordsRef.current, service_area: cityFromLocation.toLowerCase(), page: 1 };
    if (selectedSuggestion) {
      if (selectedSuggestion.taxonomy_ids) {
        searchParams.taxonomy_code = selectedSuggestion.taxonomy_ids;
      } else if (selectedSuggestion.code) {
        searchParams.taxonomy_code = [selectedSuggestion.code];
      }
    }
    doSearch(searchParams);
  }

  function handleLocationCancel() {
    setShowLocationConfirm(false);
    setLocationInput("");
    setParams({ location: "", coords: null, service_area: null });
  }

  return (
    <div className="transparent-search">
      <form className="transparent-group" onSubmit={handleSubmit}>
        <div className="inquiry">What do you need help with today?</div>
        <div className="search-label">
          Search resources in {stateName}
          <br />
          <span className="search-label-link">
            Outside {stateName}?{" "}
            <a href="http://www.211.org">Find your local 211</a>
          </span>
        </div>
        <div className="form-wrap" ref={formWrapRef}>
          <div className="inline-wrap search-input-region">
            <SearchInput
              ref={suggestionsRef}
              icon={<TextSearch size={16} strokeWidth={2} />}
              inputRef={searchInputRef}
              id="search"
              name="term"
              placeholder="Search services or programs"
              value={searchInput}
              onChange={handleSearchInputChange}
              onFocus={handleSearchInputFocus}
              onBlur={handleSearchInputBlur}
              onKeyDown={handleSearchKeyDown}
              autoComplete="off"
              role="combobox"
              aria-haspopup="listbox"
              aria-autocomplete="list"
              aria-expanded={showSuggestions && suggestions.length > 0}
              aria-controls="ts-search-suggestions"
              aria-activedescendant={hoveredIndex !== null ? `ts-search-suggestions-option-${hoveredIndex}` : undefined}
            />
          </div>

          {showSuggestions && suggestions.length > 0 && (
            <ul
              id="ts-search-suggestions"
              className="ui-autocomplete ui-menu"
              role="listbox"
              ref={suggestionsListRef}
              onBlur={handleSuggestionsBlur}
              onMouseDown={handleSuggestionsMouseDown}
            >
              {suggestions.map((s, i) => {
                const prevType = i > 0 ? suggestions[i - 1].type : null;
                const showSuggestedHeader = s.type === "suggested" && prevType !== "suggested";
                const highlighted = highlightTerm(s.label, searchInput);
                return (
                  <React.Fragment key={i}>
                    {showSuggestedHeader && (
                      <li className="ui-autocomplete-category label" aria-hidden="true">Suggested Search</li>
                    )}
                    <li
                      id={`ts-search-suggestions-option-${i}`}
                      className={`ui-menu-item${
                        s.type === "suggested" ? " suggested-search-item" : " category-item"
                      }${hoveredIndex === i ? " ui-state-focus" : ""}`}
                      role="option"
                      aria-selected={hoveredIndex === i}
                      tabIndex={-1}
                      onClick={() => handleSuggestionSelect(s)}
                      onMouseEnter={() => setHoveredIndex(i)}
                      onMouseLeave={() => setHoveredIndex(null)}
                    >
                      <div className="ui-menu-item-wrapper" dangerouslySetInnerHTML={{ __html: highlighted }} />
                    </li>
                  </React.Fragment>
                );
              })}
            </ul>
          )}

          <div className="inline-wrap location-region">
            <div className="helper-input">
              <PlaceAutocomplete
                hasTrailingControl
                id="location"
                name="location"
                placeholder="ZIP code, city, or address"
                value={locationInput}
                onChange={handleLocationInputChange}
                onPlaceSelect={handleLocationPlaceSelect}
              />
              <i
                ref={geolocateButtonRef}
                className={`geolocate-home geolocate ico ico-geo${geolocating ? " loading" : ""}`}
                role="button"
                tabIndex={0}
              />
            </div>
          </div>

          <button className="home-search-button btn btn-secondary large home-search-btn" name="search" type="submit">
            Search
          </button>
        </div>
      </form>

      {showLocationConfirm && (
        <div className="location-confirmation-region">
          <LocationConfirmation
            location={locationInput || stateName}
            onConfirm={handleLocationConfirm}
            onCancel={handleLocationCancel}
          />
        </div>
      )}
    </div>
  );
}

export default TransparentSearch;
